/**
 * Slot variant swatch row (VARIANTS_MVP PR2).
 *
 * Rendered inside a SlotCard's expanded detail when the slot's material color is
 * bound to a variants-bearing color property. Each chip is a flat CSS color
 * swatch; clicking one routes through `setMaterialColorForState` — the SAME call
 * the color picker makes — so state-aware routing / undo / preview-regen are
 * inherited. Variant NAMES are display-only (never referenced); clicks bake the
 * VALUE.
 *
 * Layout: one chip per authored variant, then a `+` chip to add. There is no
 * separate "default" chip — every swatch is a normal selectable value; the
 * active one (matching the property's current value) is ringed.
 *
 * The row re-reads its variant set on every editor BEHAVIOR_CHANGED for
 * Properties (edits from the Properties panel or undo/redo must reflect here).
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import {
    getMaterialVariantSet,
    addVariantOption,
    renameVariantOption,
    removeVariantOption,
    setMaterialColorForState,
    setVariantSetTransition,
} from 'trace-viewer/editor';
import type { MaterialVariantSet } from 'trace-viewer/editor';
import type { EasingFunction, TimeTransition } from 'trace-viewer/behaviors';
import type { SceneObject } from 'trace-viewer/scene';
import { EDITOR_EVENTS } from '../editor-events';

/**
 * Easing options — hardcoded to match the `EasingFunction` union in trace-viewer
 * (a type has no runtime members to enumerate). Keep in sync with that union.
 */
const EASING_OPTIONS: EasingFunction[] = ['linear', 'easeIn', 'easeOut', 'easeInOut'];

/**
 * Normalize a color to #rrggbbaa for the APPLIED value — matching
 * renderer.tsx's handleColorSelect. The native <input type="color"> and stored
 * variant values are 6-digit #rrggbb; the rest of the app applies 8-digit, so
 * append opaque alpha. (Stored variant values stay 6-digit — addVariantOption
 * strips alpha back off via toSixDigitHex.)
 */
function toAppliedColor(hex: string): string {
    return hex.length === 7 ? hex + 'FF' : hex;
}

export interface SlotVariantsRowProps {
    sceneObject: SceneObject;
    slotIndex: number;
    slotName: string;
    /** Debounced slot-preview regen — the SlotCard's `regenSlotPreview` for this slot. */
    onRegen: () => void;
}

const SWATCH = 22; // px

export const SlotVariantsRow: React.FC<SlotVariantsRowProps> = ({
    sceneObject, slotIndex, onRegen,
}) => {
    // Bump to force a re-render (and thus a re-read of the non-React variant
    // set) after any commit.
    const [, setVersion] = useState(0);
    const refresh = useCallback(() => setVersion(v => v + 1), []);

    // Re-read on any Properties change (Properties-panel edits, undo/redo).
    useEffect(() => {
        window.addEventListener(EDITOR_EVENTS.BEHAVIOR_CHANGED, refresh);
        return () => window.removeEventListener(EDITOR_EVENTS.BEHAVIOR_CHANGED, refresh);
    }, [refresh]);

    // Read live behavior data on every render — cheap, synchronous, and always
    // current. A memo keyed on a version counter would only fight the linter.
    const set: MaterialVariantSet | null = getMaterialVariantSet(sceneObject, slotIndex);

    // Hidden color input for the `+` chip's add flow. We commit on its native
    // `change` event (fires ONCE when the picker closes), not React onChange
    // (which mirrors the DOM `input` event → one commit per drag tick).
    const addInputRef = useRef<HTMLInputElement>(null);
    useEffect(() => {
        const el = addInputRef.current;
        if (!el) return;
        const onCommit = (): void => {
            const value = el.value; // native picker → #rrggbb (stored 6-digit)
            // Add the swatch, then ACTIVATE it (apply its value) so the freshly
            // added variant becomes the current selection, not just appended.
            void addVariantOption(sceneObject, propertyNameRef.current, value).then(({ errors }) => {
                if (errors.length > 0) {
                    console.warn('[SlotVariantsRow] add variant failed:', errors);
                    refresh();
                    return;
                }
                void setMaterialColorForState(sceneObject, toAppliedColor(value), { slot: slotIndex })
                    .then(({ errors: applyErrors }) => {
                        if (applyErrors.length > 0) console.warn('[SlotVariantsRow] activate new variant failed:', applyErrors);
                        else onRegen();
                        refresh();
                    });
            });
        };
        el.addEventListener('change', onCommit);
        return () => el.removeEventListener('change', onCommit);
    }, [sceneObject, slotIndex, onRegen, refresh]);

    // propertyName can change (rebind); keep it fresh for the imperative listener.
    const propertyNameRef = useRef<string>('');

    // Inline rename buffer: index being renamed + draft text.
    const [renaming, setRenaming] = useState<{ index: number; draft: string } | null>(null);
    // Right-click context menu on a swatch: which variant + where.
    const [contextMenu, setContextMenu] = useState<{ index: number; x: number; y: number } | null>(null);
    // Transition-editor popover open state + its portal anchor position. The
    // popover renders through a body portal (fixed-positioned) so it escapes the
    // slot card's `overflow: hidden` clipping.
    const [transitionOpen, setTransitionOpen] = useState(false);
    const [popoverPos, setPopoverPos] = useState<{ top: number; left: number } | null>(null);
    const chipRef = useRef<HTMLButtonElement>(null);

    if (!set) return null;
    const { propertyName, variants, currentValue } = set;
    propertyNameRef.current = propertyName;

    const apply = (value: string): void => {
        // Same call the color picker makes. Stored variant values are 6-digit;
        // apply them as #rrggbbaa to match handleColorSelect's applied format.
        void setMaterialColorForState(sceneObject, toAppliedColor(value), { slot: slotIndex })
            .then(({ errors }) => {
                if (errors.length > 0) console.warn('[SlotVariantsRow] set color failed:', errors);
                else onRegen();
                refresh();
            });
    };

    const commitRename = (): void => {
        if (!renaming) return;
        const { index, draft } = renaming;
        const trimmed = draft.trim();
        setRenaming(null);
        if (trimmed.length === 0 || trimmed === variants[index]?.name) return;
        void renameVariantOption(sceneObject, propertyName, index, trimmed).then(({ errors }) => {
            if (errors.length > 0) console.warn('[SlotVariantsRow] rename variant failed:', errors);
            refresh();
        });
    };

    const handleDelete = (index: number): void => {
        void removeVariantOption(sceneObject, propertyName, index).then(({ errors }) => {
            if (errors.length > 0) console.warn('[SlotVariantsRow] remove variant failed:', errors);
            refresh();
        });
    };

    // The set's default transition (governs how swatch clicks / graph taps animate).
    const transition = set.declaration.transition;
    const commitTransition = (next: TimeTransition | undefined): void => {
        void setVariantSetTransition(sceneObject, propertyName, next).then(({ errors }) => {
            if (errors.length > 0) console.warn('[SlotVariantsRow] set transition failed:', errors);
            refresh();
        });
    };

    const toggleTransitionPopover = (): void => {
        if (transitionOpen) { setTransitionOpen(false); return; }
        const rect = chipRef.current?.getBoundingClientRect();
        if (rect) setPopoverPos({ top: rect.bottom + 4, left: rect.left });
        setTransitionOpen(true);
    };

    return (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', marginBottom: '6px' }}>
            <span style={{ fontSize: '11px', color: '#999', minWidth: '70px', flexShrink: 0, paddingTop: '3px' }}>
                Variants
            </span>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', alignItems: 'center' }}>
                {variants.map((variant, index) => {
                    const value = variant.values[propertyName];
                    const isCurrent = index === set.activeIndex;
                    const isRenaming = renaming?.index === index;
                    return (
                        <div
                            key={index}
                            style={{ position: 'relative', display: 'inline-flex' }}
                        >
                            {isRenaming ? (
                                <input
                                    type="text"
                                    autoFocus
                                    value={renaming.draft}
                                    onChange={(e) => setRenaming({ index, draft: e.target.value })}
                                    onBlur={commitRename}
                                    onKeyDown={(e) => {
                                        if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                                        else if (e.key === 'Escape') setRenaming(null);
                                    }}
                                    style={{
                                        width: '70px', padding: '2px 4px', fontSize: '10px',
                                        backgroundColor: '#333', border: '1px solid #666',
                                        borderRadius: '3px', color: '#fff', outline: 'none',
                                    }}
                                />
                            ) : (
                                <button
                                    onClick={() => apply(value)}
                                    onDoubleClick={() => setRenaming({ index, draft: variant.name })}
                                    onContextMenu={(e) => {
                                        e.preventDefault();
                                        setContextMenu({ index, x: e.clientX, y: e.clientY });
                                    }}
                                    title={`${variant.name} (double-click to rename, right-click for options)`}
                                    style={{
                                        width: `${SWATCH}px`, height: `${SWATCH}px`, padding: 0, cursor: 'pointer',
                                        backgroundColor: value, borderRadius: '4px',
                                        border: isCurrent ? '2px solid #fff' : '1px solid #555',
                                        boxShadow: isCurrent ? '0 0 0 1px #000' : 'none',
                                    }}
                                />
                            )}
                        </div>
                    );
                })}

                {/* `+` chip — opens the native color input; commit adds a variant.
                    The input is fixed-positioned over the chip so the native picker
                    anchors to the chip and is NOT clipped by the slot card's
                    `overflow: hidden`. It's kept invisible (opacity 0) at the chip. */}
                <button
                    onClick={() => addInputRef.current?.click()}
                    title="Add variant from a color"
                    style={{
                        position: 'relative',
                        width: `${SWATCH}px`, height: `${SWATCH}px`, padding: 0, cursor: 'pointer',
                        backgroundColor: 'transparent', color: '#888',
                        border: '1px dashed #555', borderRadius: '4px',
                        fontSize: '14px', lineHeight: `${SWATCH - 2}px`,
                    }}
                >
                    +
                    <input
                        ref={addInputRef}
                        type="color"
                        defaultValue={currentValue && currentValue.length >= 7 ? currentValue.slice(0, 7) : '#888888'}
                        style={{
                            position: 'absolute', top: 0, left: 0,
                            width: '100%', height: '100%', padding: 0, margin: 0,
                            border: 'none', opacity: 0, cursor: 'pointer',
                        }}
                    />
                </button>

                {/* Transition chip — shows the set's default transition; click to edit. */}
                <button
                    ref={chipRef}
                    onClick={toggleTransitionPopover}
                    title="Variant switch transition"
                    style={{
                        marginLeft: '4px',
                        height: `${SWATCH}px`, padding: '0 6px', cursor: 'pointer',
                        fontSize: '10px', color: transition ? '#ddd' : '#777',
                        backgroundColor: 'transparent', border: '1px solid #555',
                        borderRadius: '4px', whiteSpace: 'nowrap',
                    }}
                >
                    {transition ? `${transition.duration}s` : '–'}
                </button>
            </div>

            {/* Popover rendered through a body portal (fixed) so it escapes the
                slot card's `overflow: hidden`. */}
            {transitionOpen && popoverPos && createPortal(
                <>
                    {/* Click-away backdrop. */}
                    <div
                        onClick={() => setTransitionOpen(false)}
                        style={{ position: 'fixed', inset: 0, zIndex: 999 }}
                    />
                    <div
                        style={{
                            position: 'fixed', top: `${popoverPos.top}px`, left: `${popoverPos.left}px`,
                            zIndex: 1000,
                            display: 'flex', flexDirection: 'column', gap: '6px',
                            padding: '8px', minWidth: '150px',
                            backgroundColor: '#2a2a2a', border: '1px solid #555',
                            borderRadius: '4px', boxShadow: '0 2px 8px rgba(0,0,0,0.5)',
                        }}
                    >
                        <label style={{ fontSize: '10px', color: '#999', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            Duration (s)
                            <input
                                type="number"
                                step={0.1}
                                min={0}
                                value={transition?.duration ?? 0.3}
                                onChange={(e) => {
                                    const duration = Math.max(0, Number(e.target.value) || 0);
                                    commitTransition({ duration, easing: transition?.easing ?? 'easeOut' });
                                }}
                                style={{
                                    padding: '3px 4px', fontSize: '11px', color: '#fff',
                                    backgroundColor: '#333', border: '1px solid #666',
                                    borderRadius: '3px', outline: 'none',
                                }}
                            />
                        </label>
                        <label style={{ fontSize: '10px', color: '#999', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            Easing
                            <select
                                value={transition?.easing ?? 'easeOut'}
                                onChange={(e) => {
                                    commitTransition({
                                        duration: transition?.duration ?? 0.3,
                                        easing: e.target.value as EasingFunction,
                                    });
                                }}
                                style={{
                                    padding: '3px 4px', fontSize: '11px', color: '#fff',
                                    backgroundColor: '#333', border: '1px solid #666',
                                    borderRadius: '3px', outline: 'none',
                                }}
                            >
                                {EASING_OPTIONS.map(opt => (
                                    <option key={opt} value={opt}>{opt}</option>
                                ))}
                            </select>
                        </label>
                        <button
                            onClick={() => { commitTransition(undefined); setTransitionOpen(false); }}
                            title="No transition — switches snap"
                            style={{
                                padding: '3px 4px', fontSize: '10px', cursor: 'pointer',
                                color: '#ddd', backgroundColor: '#444',
                                border: '1px solid #666', borderRadius: '3px',
                            }}
                        >
                            None (snap)
                        </button>
                    </div>
                </>,
                document.body,
            )}

            {/* Swatch right-click menu (body portal, fixed at the cursor). */}
            {contextMenu && createPortal(
                <>
                    <div
                        onClick={() => setContextMenu(null)}
                        onContextMenu={(e) => { e.preventDefault(); setContextMenu(null); }}
                        style={{ position: 'fixed', inset: 0, zIndex: 999 }}
                    />
                    <div
                        style={{
                            position: 'fixed', top: `${contextMenu.y}px`, left: `${contextMenu.x}px`,
                            zIndex: 1000, minWidth: '120px',
                            display: 'flex', flexDirection: 'column',
                            padding: '4px', backgroundColor: '#2a2a2a',
                            border: '1px solid #555', borderRadius: '4px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.5)',
                        }}
                    >
                        {[
                            {
                                label: 'Rename',
                                action: () => setRenaming({ index: contextMenu.index, draft: variants[contextMenu.index]?.name ?? '' }),
                            },
                            {
                                label: 'Delete',
                                action: () => handleDelete(contextMenu.index),
                                danger: true,
                            },
                        ].map(item => (
                            <button
                                key={item.label}
                                onClick={() => { item.action(); setContextMenu(null); }}
                                style={{
                                    textAlign: 'left', padding: '5px 8px', fontSize: '11px',
                                    cursor: 'pointer', border: 'none', borderRadius: '3px',
                                    backgroundColor: 'transparent',
                                    color: item.danger ? '#e57373' : '#ddd',
                                }}
                                onMouseEnter={(e) => { (e.currentTarget).style.backgroundColor = '#3a3a3a'; }}
                                onMouseLeave={(e) => { (e.currentTarget).style.backgroundColor = 'transparent'; }}
                            >
                                {item.label}
                            </button>
                        ))}
                    </div>
                </>,
                document.body,
            )}
        </div>
    );
};
